import { useCallback, useContext } from 'react';
import { RangeType } from '@/FiltersMenu/Budget/ExperimentBudget/constants';
import AppliedFiltersContext from '~/contexts/AppliedFiltersContext';
import { useFinanceInfo } from '~/contexts/FinanceAndTradeContext';
import { type RangeInputState } from '../../RangeInput/useRangeInput';

const IMPOSSIBLE_DOWN_PAYMENT = -1;
const IMPOSSIBLE_MAX_MONTHLY_PAYMENT = 999999;
const DEFAULT_CASH_DOWN_TO_APPLY = 10000;

export default function useUpdatePaymentFilters(values: RangeInputState) {
  const { updateRangeFilter } = useContext(AppliedFiltersContext);
  const { maxMonthlyPayment, minDownPayment } = useFinanceInfo();

  return useCallback(
    (rangeType: RangeType, value: number) => {
      const currentDownPaymentFilter =
        values?.downPayment ?? IMPOSSIBLE_DOWN_PAYMENT;
      const currentDownPaymentOrDefaultCashDownToApply =
        currentDownPaymentFilter >= minDownPayment
          ? values.downPayment
          : DEFAULT_CASH_DOWN_TO_APPLY;

      const currentMonthlyPaymentFilter =
        values?.monthlyPayment ?? IMPOSSIBLE_MAX_MONTHLY_PAYMENT;
      const currentMonthlyPaymentOrDefaultMaxMonthlyToApply =
        currentMonthlyPaymentFilter <= maxMonthlyPayment
          ? values?.monthlyPayment
          : maxMonthlyPayment;

      const defaultValuesToApply = {
        downPayment: currentDownPaymentOrDefaultCashDownToApply,
        monthlyPayment: currentMonthlyPaymentOrDefaultMaxMonthlyToApply
      };

      const shouldUseDefault = value === 0;

      if (shouldUseDefault) {
        updateRangeFilter({
          filterKey: 'finance',
          value: { ...defaultValuesToApply }
        });
      } else {
        updateRangeFilter({
          filterKey: 'finance',
          value: { ...defaultValuesToApply, [rangeType]: value }
        });
      }
    },
    [maxMonthlyPayment, minDownPayment, updateRangeFilter, values]
  );
}
